import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import FolderIcon from '@material-ui/icons/Folder';
import GetAppIcon from '@material-ui/icons/GetApp';
import HistoryIcon from '@material-ui/icons/History';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import RefreshIcon from '@material-ui/icons/Refresh';
import StopIcon from '@material-ui/icons/Stop';
import { Page, Header, Content, Progress, WarningPanel } from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { Artifact, ArtifactTable, Folder } from './ArtifactTable';
import { UploadArtifactDialog } from './UploadArtifactDialog';
import { CreateFolderDialog } from './CreateFolderDialog';
// Removed unused ROOT_TARGET_LABEL import

export const PersistenceRepositoryPage = () => {
  const theme = useTheme();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [activeTab, setActiveTab] = useState(0);

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [showUploadChoice, setShowUploadChoice] = useState(false);
  const [showFolderSelect, setShowFolderSelect] = useState(false);
  const [tempFolderId, setTempFolderId] = useState<string | null>(null);

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<{ id: string | null; name: string }[]>([
    { id: null, name: 'Root' },
  ]);

  // Current user info
  const [currentUserRef, setCurrentUserRef] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMaintainer, setIsMaintainer] = useState(false);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [isGuest, setIsGuest] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterUseCase, setFilterUseCase] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // State for selected upload folder (null for root)
  const [selectedUploadFolderId, setSelectedUploadFolderId] = useState<string | null>(null);

  const uploadFolderMap = useMemo(() => {
    const map: Record<string, Folder> = {};
    folders.forEach(folder => {
      map[folder.id] = folder;
    });
    return map;
  }, [folders]);

  const selectableUploadFolders = useMemo(() => {
    return folders.filter(folder => {
      if (folder.is_public === false && folder.created_by !== currentUserRef) {
        return false;
      }
      return true;
    });
  }, [folders, currentUserRef]);

  const getUploadFolderLabel = useCallback((folderId: string): string => {
    const folder = uploadFolderMap[folderId];
    if (!folder) return folderId;

    const names: string[] = [];
    let cursor: Folder | undefined = folder;
    const seen = new Set<string>();
    while (cursor) {
      if (seen.has(cursor.id)) break;
      seen.add(cursor.id);
      names.unshift(cursor.name);
      cursor = cursor.parent_id ? uploadFolderMap[cursor.parent_id] : undefined;
    }
    return names.join(' / ');
  }, [uploadFolderMap]);

  // Debounce text search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(filterQ), 300);
    return () => clearTimeout(timer);
  }, [filterQ]);

  // Fetch current user identity and role flags from the backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('persistence');
        const { token } = await identityApi.getCredentials();
        const resp = await fetch(`${baseUrl}/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        setCurrentUserRef(data.entityRef ?? '');
        setIsAdmin(!!data.isAdmin);
        setIsMaintainer(!!data.isMaintainer);
        setIsDeveloper(!!data.isDeveloper);
        setIsGuest(!!data.isGuest);
      } catch { /* silently ignore — defaults remain false */ }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, identityApi]);

  // ── MATLAB Workspaces tab state ─────────────────────────────────────────
  interface MatlabFolder {
    id: number | null;
    owner_username: string;
    folder_name: string;
    is_public: boolean;
    last_saved_at: string | null;
    created_at: string | null;
    active_session_id: string | null;
    locked_by: string | null;
    is_mine: boolean;
  }
  interface WorkspaceContentItem {
    name: string;
    path: string;
    isDirectory: boolean;
    size: number | null;
  }
  const [matlabFolders, setMatlabFolders] = useState<MatlabFolder[]>([]);
  const [matlabLoading, setMatlabLoading] = useState(false);
  const [matlabError, setMatlabError] = useState<string | null>(null);
  const [matlabAction, setMatlabAction] = useState<string | null>(null); // folder_name being acted on
  const [matlabDownloading, setMatlabDownloading] = useState<Set<number>>(new Set());
  const matlabFolderUploadInputRef = useRef<HTMLInputElement>(null);
  const [matlabUploadingFolder, setMatlabUploadingFolder] = useState<string | null>(null);
  const workspaceFileUploadRef = useRef<HTMLInputElement>(null);
  const [uploadingToWorkspace, setUploadingToWorkspace] = useState(false);
  const [selectedWorkspaceFolder, setSelectedWorkspaceFolder] = useState<MatlabFolder | null>(null);
  const [workspaceFolderContents, setWorkspaceFolderContents] = useState<WorkspaceContentItem[]>([]);
  const [browseFolderLoading, setBrowseFolderLoading] = useState(false);
  const [currentBrowsePath, setCurrentBrowsePath] = useState<string>('');
  const [deletingPath, setDeletingPath] = useState<string | null>(null);

  const handleMatlabDownload = useCallback(async (f: MatlabFolder) => {
    if (!f.id) return;
    const folderId = f.id;
    setMatlabDownloading(prev => new Set(prev).add(folderId));
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/workspace/${folderId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(body.error ?? 'Download failed');
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${f.folder_name}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('Network error during download');
    } finally {
      setMatlabDownloading(prev => { const n = new Set(prev); n.delete(folderId); return n; });
    }
  }, [discoveryApi, identityApi]);

  const fetchMatlabFolders = useCallback(async () => {
    setMatlabLoading(true);
    setMatlabError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/workspaces/available-folders`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Failed to load MATLAB workspaces');
      const data = await resp.json();
      setMatlabFolders(data.folders ?? []);
    } catch (e: any) {
      setMatlabError(e.message ?? 'Unknown error');
    } finally {
      setMatlabLoading(false);
    }
  }, [discoveryApi, identityApi]);

  const handleMatlabToggleVisibility = useCallback(async (f: MatlabFolder) => {
    if (!f.id) return; // not yet registered
    setMatlabAction(f.folder_name);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(
        `${baseUrl}/workspace/${f.id}/visibility`,   // ✅ use id
        {
          method: 'PATCH',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (resp.ok) {
        const data = await resp.json();
        setMatlabFolders(prev => prev.map(x =>
          x.id === f.id ? { ...x, is_public: data.is_public } : x,
        ));
      }
    } catch { /* ignore */ }
    finally { setMatlabAction(null); }
  }, [discoveryApi, identityApi]);

  const handleMatlabDelete = useCallback(async (f: MatlabFolder) => {
    if (!f.id) return;
    if (!window.confirm(`Delete folder "${f.folder_name}" and all its files? This cannot be undone.`)) return;
    setMatlabAction(f.folder_name);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(
        `${baseUrl}/workspace/${f.id}`,
        { method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (resp.ok) {
        setMatlabFolders(prev => prev.filter(x => x.id !== f.id));
      } else {
        const body = await resp.json().catch(() => ({}));
        alert(body.message ?? body.error ?? 'Failed to delete');
      }
    } catch { alert('Network error'); }
    finally { setMatlabAction(null); }
  }, [discoveryApi, identityApi]);

  const handleMatlabEndSession = useCallback(async (f: MatlabFolder) => {
    if (!f.id) return;
    setMatlabAction(f.folder_name);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      await fetch(`${baseUrl}/workspaces/end-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          workspace_id: f.id,
          comment: 'session ended by workspace owner',
        }),
      });
      setMatlabFolders(prev => prev.map(x =>
        x.id === f.id ? { ...x, active_session_id: null } : x,
      ));
    } catch { /* ignore */ }
    finally { setMatlabAction(null); }
  }, [discoveryApi, identityApi]);

  const onMatlabFolderSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const firstRel = ((files[0] as any).webkitRelativePath as string) || files[0].name;
    const topFolder = firstRel.split('/').filter(Boolean)[0] || 'workspace';

    setMatlabUploadingFolder(topFolder);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const form = new FormData();
      const relPaths: string[] = [];

      for (const f of files) {
        form.append('files', f);
        const rel = ((f as any).webkitRelativePath as string) || f.name;
        relPaths.push(rel);
      }
      form.append('paths', JSON.stringify(relPaths));
      form.append('folder_name', topFolder);

      const resp = await fetch(`${baseUrl}/workspaces/import-folder`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        alert(body.error ?? 'Failed to upload folder as new workspace');
      } else {
        const data = await resp.json().catch(() => ({}));
        await fetchMatlabFolders();
        if (data?.folder_name) {
          alert(`Folder uploaded as new workspace: ${data.folder_name}`);
        }
      }
    } catch {
      alert('Network error during workspace import');
    } finally {
      setMatlabUploadingFolder(null);
      if (matlabFolderUploadInputRef.current) matlabFolderUploadInputRef.current.value = '';
    }
  }, [
    discoveryApi,
    fetchMatlabFolders,
    identityApi,
  ]);

  const loadWorkspaceFolderContents = useCallback(async (folder: MatlabFolder, folderPath: string) => {
    if (!folder.id) return;
    setBrowseFolderLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const query = folderPath ? `?path=${encodeURIComponent(folderPath)}` : '';
      const resp = await fetch(`${baseUrl}/workspace/${folder.id}/list-contents${query}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setWorkspaceFolderContents((data.items || []) as WorkspaceContentItem[]);
        setCurrentBrowsePath(folderPath);
      }
    } catch {
      alert('Failed to load folder contents');
    } finally {
      setBrowseFolderLoading(false);
    }
  }, [discoveryApi, identityApi]);

  const browseWorkspaceFolder = useCallback(async (folder: MatlabFolder) => {
    setSelectedWorkspaceFolder(folder);
    await loadWorkspaceFolderContents(folder, '');
  }, [loadWorkspaceFolderContents]);

  const navigateWorkspaceFolder = useCallback(async (folder: MatlabFolder, itemPath: string) => {
    await loadWorkspaceFolderContents(folder, itemPath);
  }, [loadWorkspaceFolderContents]);

  const deleteWorkspaceFolder = useCallback(async (folder: MatlabFolder, folderPath: string) => {
    if (!folder.id || !folderPath) return;
    if (!window.confirm(`Delete "${folderPath}"?`)) return;

    setDeletingPath(folderPath);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/workspace/${folder.id}/folder`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ folder_path: folderPath }),
      });
      if (resp.ok) {
        // Refresh contents
        await loadWorkspaceFolderContents(folder, currentBrowsePath);
      } else {
        alert('Failed to delete folder');
      }
    } catch {
      alert('Network error during deletion');
    } finally {
      setDeletingPath(null);
    }
  }, [discoveryApi, identityApi, currentBrowsePath, loadWorkspaceFolderContents]);

  // fetchArtifacts implementation (placed before deleteWorkspaceFile)
  const fetchArtifacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterUseCase) params.set('useCase', filterUseCase);
      if (debouncedQ) {
        params.set('q', debouncedQ);
        // When searching, don't filter by folder (search across all)
      } else {
        params.set('folder_id', currentFolderId ?? 'root');
      }
      // Exclude documents from the main artifact list (they belong in the Documentation tab)
      //params.set('excludeType', 'document');

      const [artifactRes, folderRes] = await Promise.all([
        fetch(`${baseUrl}/artifacts?${params.toString()}`, { headers }),
        fetch(`${baseUrl}/folders?parent_id=${currentFolderId ?? 'root'}`, { headers }),
      ]);

      if (!artifactRes.ok) {
        const err = await artifactRes.json().catch(() => ({ error: artifactRes.statusText }));
        throw new Error(err.error ?? 'Failed to load artifacts');
      }

      const artifactData = await artifactRes.json();
      setArtifacts(artifactData.artifacts ?? []);

      if (folderRes.ok) {
        const folderData = await folderRes.json();
        setFolders(folderData.folders ?? []);
      }
    } catch (err: any) {
      setError(err.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi, filterType, filterUseCase, debouncedQ, currentFolderId]);

  const deleteWorkspaceFile = useCallback(async (folder: MatlabFolder, filePath: string) => {
    if (!folder.id || !filePath) return;
    if (!window.confirm(`Delete file "${filePath}"?`)) return;

    setDeletingPath(filePath);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/workspace/${folder.id}/file`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ file_path: filePath }),
      });
      if (resp.ok) {
        await loadWorkspaceFolderContents(folder, currentBrowsePath);
      } else {
        alert('Failed to delete file');
      }
    } catch (e) {
      alert('Error deleting file');
    } finally {
      setDeletingPath(null);
    }
  }, [discoveryApi, identityApi, loadWorkspaceFolderContents, currentBrowsePath, fetchArtifacts]);

  const uploadFilesToWorkspace = useCallback(async (files: File[]) => {
    if (!selectedWorkspaceFolder?.id || files.length === 0) return;
    setUploadingToWorkspace(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      const form = new FormData();
      const relPaths: string[] = [];
      for (const f of files) {
        form.append('files', f);
        relPaths.push(currentBrowsePath ? `${currentBrowsePath}/${f.name}` : f.name);
      }
      form.append('paths', JSON.stringify(relPaths));
      const resp = await fetch(`${baseUrl}/workspace/${selectedWorkspaceFolder.id}/upload-files`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (resp.ok) {
        await loadWorkspaceFolderContents(selectedWorkspaceFolder, currentBrowsePath);
      } else {
        const body = await resp.json().catch(() => ({}));
        alert(body.error ?? 'Failed to upload files');
      }
    } catch {
      alert('Error uploading files');
    } finally {
      setUploadingToWorkspace(false);
      if (workspaceFileUploadRef.current) workspaceFileUploadRef.current.value = '';
    }
  }, [selectedWorkspaceFolder, currentBrowsePath, discoveryApi, identityApi, loadWorkspaceFolderContents]);

  useEffect(() => {
    if (activeTab === 0) fetchArtifacts();
  }, [activeTab, fetchArtifacts]);

  useEffect(() => {
    if (activeTab === 1) fetchMatlabFolders();
  }, [activeTab, fetchMatlabFolders]);

  // History panel per folder
  interface HistoryRecord { id: number; changed_by: string; comment: string; changed_at: string; }
  const [expandedFolder, setExpandedFolder] = useState<string | null>(null);
  const [historyMap, setHistoryMap] = useState<Record<string, HistoryRecord[]>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);

  const toggleHistory = useCallback(async (f: MatlabFolder) => {
  const key = f.folder_name;

  // Collapse if already open
  if (expandedFolder === key) {
    setExpandedFolder(null);
    return;
  }

  setExpandedFolder(key);

  if (!f.id) return; 
  setHistoryLoading(key);
  try {
    const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
    const { token } = await identityApi.getCredentials();

    const resp = await fetch(
      `${baseUrl}/workspace/${f.id}/history`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );

    if (resp.ok) {
      const data = await resp.json();
      setHistoryMap(prev => ({ ...prev, [key]: data.history ?? [] }));
    } else {
      setHistoryMap(prev => ({ ...prev, [key]: [] }));
    }
  } catch {
    setHistoryMap(prev => ({ ...prev, [key]: [] }));
  } finally {
    setHistoryLoading(null);
  }
}, [expandedFolder, discoveryApi, identityApi]); 

  // Invalidate history cache when visibility or delete changes a folder
  const invalidateHistory = useCallback((folderName: string) => {
    setHistoryMap(prev => {
      const n = { ...prev };
      delete n[folderName];
      return n;
    });
    setExpandedFolder(prev => prev === folderName ? null : prev);
  }, []);


  const canUpload = isAdmin || isMaintainer || isDeveloper || isGuest;

  const navigateToFolder = async (folderId: string | null, folderName: string) => {
    if (folderId === null) {
      // Navigate to root
      setCurrentFolderId(null);
      setFolderPath([{ id: null, name: 'Root' }]);
    } else {
      setCurrentFolderId(folderId);
      // Check if we're navigating back via breadcrumb
      const idx = folderPath.findIndex(f => f.id === folderId);
      if (idx >= 0) {
        setFolderPath(folderPath.slice(0, idx + 1));
      } else {
        setFolderPath([...folderPath, { id: folderId, name: folderName }]);
      }
    }
  };

  // Open the upload choice dialog when user clicks Upload
  const openUploadChoice = () => {
    setShowUploadChoice(true);
  };

  // Upload to root (no folder)
  const uploadToRoot = () => {
    setSelectedUploadFolderId(null);
    setUploadOpen(true);
    setShowUploadChoice(false);
  };

  // Upload to existing folder – always opens folder selection popup.
  const uploadToExistingFolder = () => {
    // Force explicit user choice: do not preselect any folder.
    setTempFolderId(null);
    setShowFolderSelect(true);
    setShowUploadChoice(false);
  };

  return (
    <Page themeId="home">
      <Header
        title="Persistence Repository"
        subtitle="Store and manage large artifacts outside Git version control"
      />
      <Content>
        {/* ── Tab switcher ── */}
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Tab label="Artifacts" />
          <Tab label="MATLAB Workspaces" />
        </Tabs>

        {/* ══ TAB 0: Artifacts (original content) ══ */}
        {activeTab === 0 && (
          <>
            {/* Breadcrumb navigation */}
            <Breadcrumbs aria-label="folder navigation" style={{ marginBottom: 12 }}>
              {folderPath.map((crumb, idx) => {
                const isLast = idx === folderPath.length - 1;
                return isLast ? (
                  <span key={crumb.id ?? 'root'} style={{ fontWeight: 500 }}>{crumb.name}</span>
                ) : (
                  <Link
                    key={crumb.id ?? 'root'}
                    color="inherit"
                    href="#"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      navigateToFolder(crumb.id, crumb.name);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {crumb.name}
                  </Link>
                );
              })}
            </Breadcrumbs>

            <Grid container spacing={2} style={{ marginBottom: 16 }} alignItems="center">
              {/* Filters */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={filterQ}
                  onChange={e => setFilterQ(e.target.value)}
                  placeholder="Name, description, category…"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Artifact Type</InputLabel>
                  <Select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value as string)}
                    label="Artifact Type"
                  >
                    <MenuItem value="">All types</MenuItem>
                    <MenuItem value="binary_file">Binary File</MenuItem>
                    <MenuItem value="tabular_data">Tabular Data</MenuItem>
                    <MenuItem value="document">Document</MenuItem>
                    <MenuItem value="source_code">Source Code</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="configuration">Configuration</MenuItem>
                    <MenuItem value="model_weights">Model Weights</MenuItem>
                    <MenuItem value="dataset">Dataset</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Use Case</InputLabel>
                  <Select
                    value={filterUseCase}
                    onChange={e => setFilterUseCase(e.target.value as string)}
                    label="Use Case"
                  >
                    <MenuItem value="">All use cases</MenuItem>
                    <MenuItem value="UC1">UC1</MenuItem>
                    <MenuItem value="UC2">UC2</MenuItem>
                    <MenuItem value="UC3">UC3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={5} style={{ textAlign: 'right' }}>
                {canUpload && (
                  <>
                    <Button
                      variant="outlined"
                      color="default"
                      startIcon={<CreateNewFolderIcon />}
                      onClick={() => setCreateFolderOpen(true)}
                      style={{ marginRight: 8 }}
                    >
                      New Folder
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CloudUploadIcon />}
                      onClick={openUploadChoice}
                      style={{ marginRight: 8 }}
                    >
                      Upload Artifact
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {loading && <Progress />}
            {!loading && error && (
              <WarningPanel title="Failed to load artifacts" message={error} />
            )}
            {!loading && !error && (
              <ArtifactTable
                artifacts={artifacts}
                folders={folders}
                currentUserRef={currentUserRef}
                isAdmin={isAdmin}
                isMaintainer={isMaintainer}
                isDeveloper={isDeveloper}
                isGuest={isGuest}
                onDeleted={fetchArtifacts}
                onFolderClick={navigateToFolder}
              />
            )}

            <UploadArtifactDialog
              open={uploadOpen}
              onClose={() => setUploadOpen(false)}
              onUploaded={fetchArtifacts}
              folderId={selectedUploadFolderId}
              folders={folders}
            />
            <CreateFolderDialog
              open={createFolderOpen}
              onClose={() => setCreateFolderOpen(false)}
              onCreated={fetchArtifacts}
              parentFolderId={currentFolderId}
            />
            {/* Upload choice dialog with two buttons */}
            <Dialog open={showUploadChoice} onClose={() => setShowUploadChoice(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Upload Artifact</DialogTitle>
              <DialogContent>
                <Typography variant="body2">Choose where to upload the artifact.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={uploadToRoot} color="primary" variant="contained">Upload to Root</Button>
                <Button onClick={uploadToExistingFolder} color="default" variant="outlined">Upload to Existing Folder</Button>
              </DialogActions>
            </Dialog>

            {/* New dialog to select specific folder when uploading to existing folder */}
            <Dialog open={showFolderSelect} onClose={() => setShowFolderSelect(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Select Destination Folder</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                  Choose the folder where the artifact should be uploaded.
                </Typography>
                <FormControl variant="outlined" size="small" fullWidth>
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: 'block' }}>
                    Target folder
                  </Typography>
                  <Select
                    value={tempFolderId ?? ''}
                    onChange={e => setTempFolderId(e.target.value as string || null)}
                    displayEmpty
                    renderValue={(selected: unknown) => {
                      if (!selected) {
                        return <em style={{ color: 'rgba(255,255,255,0.54)' }}>Select target folder</em>;
                      }
                      return getUploadFolderLabel(selected as string);
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select target folder</em>
                    </MenuItem>
                    {selectableUploadFolders.map(f => (
                      <MenuItem key={f.id} value={f.id}>{getUploadFolderLabel(f.id)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectableUploadFolders.length === 0 && (
                  <Typography variant="caption" color="error" style={{ marginTop: 10, display: 'block' }}>
                    No visible folders are available. Create a folder first or upload to root.
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setShowFolderSelect(false); setShowUploadChoice(true); }} color="default">Back</Button>
                <Button
                  onClick={() => {
                    if (!tempFolderId) return;
                    setSelectedUploadFolderId(tempFolderId);
                    setUploadOpen(true);
                    setShowFolderSelect(false);
                  }}
                  color="primary"
                  variant="contained"
                  disabled={!tempFolderId || selectableUploadFolders.length === 0}
                >
                  Proceed
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {/* ══ TAB 1: MATLAB Workspaces ══ */}
        {activeTab === 1 && (
          <>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <Typography variant="body2" color="textSecondary">
                Your MATLAB VNC workspace folders. Toggle visibility to share with others or keep private.
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tooltip
                  title={
                    matlabUploadingFolder
                      ? `Uploading ${matlabUploadingFolder}...`
                      : 'Upload a local folder as a new MATLAB workspace'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={
                        matlabUploadingFolder
                          ? <CircularProgress size={14} />
                          : <CloudUploadIcon />
                      }
                      disabled={!!matlabUploadingFolder}
                      onClick={() => matlabFolderUploadInputRef.current?.click()}
                    >
                      Upload Folder
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={matlabLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={fetchMatlabFolders}
                  disabled={matlabLoading}
                >
                  Refresh
                </Button>
              </div>
            </div>

            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 10 }}>
              Uploading a local folder creates a new MATLAB workspace. Existing workspaces are not modified.
            </Typography>

            {matlabError && <WarningPanel title="Failed to load MATLAB workspaces" message={matlabError} />}

            {!matlabError && (
              <div style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 4, overflow: 'hidden' }}>
                {/* Column headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 100px 190px 90px 160px',
                  padding: '8px 16px',
                  background: theme.palette.background.default,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Folder Name</Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Visibility</Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Last Saved</Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.text.primary }}>Actions</Typography>
                </div>

                {matlabLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                    <CircularProgress size={28} />
                  </div>
                )}

                {!matlabLoading && matlabFolders.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 24px' }}>
                    <FolderIcon style={{ fontSize: 48, color: theme.palette.text.disabled }} />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                      No MATLAB workspace folders yet. Launch MATLAB Workspace from the Home page to create one.
                    </Typography>
                  </div>
                )}

                {!matlabLoading && matlabFolders.map((f, idx) => {
                  const inUse = !!f.locked_by;
                  const isActing = matlabAction === f.folder_name;
                  const isOwner = f.is_mine;
                  const isDownloading = f.id !== null && matlabDownloading.has(f.id);
                  const isExpanded = expandedFolder === f.folder_name;
                  const isHistLoading = historyLoading === f.folder_name;
                  const history = historyMap[f.folder_name] ?? null;
                  const savedAt = f.last_saved_at
                    ? new Date(f.last_saved_at).toLocaleString()
                    : f.created_at
                    ? `Created ${new Date(f.created_at).toLocaleString()}`
                    : 'Not yet saved';
                  return (
                    <div key={f.folder_name} style={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      background: idx % 2 === 0 ? theme.palette.background.paper : theme.palette.background.default,
                    }}>
                      {/* Main row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 100px 190px 90px 160px',
                        alignItems: 'center',
                        padding: '10px 16px',
                      }}>
                        {/* Name */}
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, cursor: f.id ? 'pointer' : 'default' }}
                          onClick={() => { if (f.id) browseWorkspaceFolder(f); }}
                        >
                          <FolderIcon fontSize="small" style={{ color: f.last_saved_at ? '#f9a825' : theme.palette.action.disabled, flexShrink: 0 }} />
                          <Typography variant="body2" style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: f.id ? theme.palette.primary.main : undefined }}>
                            {f.folder_name}
                          </Typography>
                          {inUse && <Chip label="In Use" size="small" style={{ background: theme.palette.success.main, color: theme.palette.success.contrastText, height: 18, fontSize: 10, fontWeight: 700, marginLeft: 4, flexShrink: 0 }} />}
                        </div>
                        {/* Visibility */}
                        <div>
                          {f.is_public
                            ? <Chip label="Public" size="small" color="primary" style={{ height: 20, fontSize: 11 }} />
                            : <Chip label="Private" size="small" variant="outlined" style={{ height: 20, fontSize: 11 }} />
                          }
                        </div>
                        {/* Last Saved */}
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                          {savedAt}
                        </Typography>
                        {/* Actions */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {isActing ? (
                            <CircularProgress size={18} style={{ margin: '0 6px' }} />
                          ) : (
                            <>
                              <Tooltip title="View commit history">
                                <IconButton size="small" onClick={() => toggleHistory(f)}
                                  style={{ color: isExpanded ? theme.palette.primary.main : theme.palette.text.disabled }}>
                                  {isExpanded ? <ExpandLessIcon style={{ fontSize: 18 }} /> : <HistoryIcon style={{ fontSize: 18 }} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={!f.id ? 'Not yet registered — launch once to enable download' : isDownloading ? 'Downloading…' : 'Download folder as ZIP'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!f.id || isDownloading}
                                    onClick={() => handleMatlabDownload(f)}
                                    style={{ color: !f.id ? theme.palette.action.disabled : theme.palette.success.main }}
                                  >
                                    {isDownloading
                                      ? <CircularProgress size={16} />
                                      : <GetAppIcon style={{ fontSize: 18 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {isOwner && (
                                <Tooltip title={inUse ? 'End the active session on this folder' : (f.is_public ? 'Make private' : 'Make public')}>
                                  <span>
                                    {inUse ? (
                                      <IconButton size="small" disabled={!!isActing} onClick={() => handleMatlabEndSession(f)}
                                        style={{ color: isActing ? theme.palette.action.disabled : theme.palette.warning.main }}>
                                        <StopIcon style={{ fontSize: 18 }} />
                                      </IconButton>
                                    ) : (
                                      <IconButton size="small" onClick={() => handleMatlabToggleVisibility(f)}
                                        style={{ color: f.is_public ? theme.palette.primary.main : theme.palette.text.disabled }}>
                                        {f.is_public ? <LockOpenIcon style={{ fontSize: 18 }} /> : <LockIcon style={{ fontSize: 18 }} />}
                                      </IconButton>
                                    )}
                                  </span>
                                </Tooltip>
                              )}
                              {isOwner && (
                                <Tooltip title={inUse ? 'Cannot delete: session is active' : !f.id ? 'Not yet registered' : 'Delete folder'}>
                                  <span>
                                    <IconButton size="small" disabled={inUse || !f.id} onClick={() => { invalidateHistory(f.folder_name); handleMatlabDelete(f); }}
                                      style={{ color: (inUse || !f.id) ? theme.palette.action.disabled : theme.palette.error.main }}>
                                      <DeleteIcon style={{ fontSize: 18 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* History panel — GitLab-style commit log */}
                      <Collapse in={isExpanded} unmountOnExit>
                        <div style={{ borderTop: `1px solid ${theme.palette.divider}`, background: theme.palette.background.default }}>
                          {/* History header */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px 6px', borderBottom: `1px solid ${theme.palette.divider}` }}>
                            <HistoryIcon style={{ fontSize: 16, color: theme.palette.primary.main }} />
                            <Typography variant="caption" style={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: 12 }}>
                              Commit History — {f.folder_name}
                            </Typography>
                          </div>

                          {isHistLoading && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                              <CircularProgress size={20} />
                            </div>
                          )}

                          {!isHistLoading && history !== null && history.length === 0 && (
                            <div style={{ padding: '12px 20px' }}>
                              <Typography variant="caption" color="textSecondary">No commits yet — session logs will appear here after each "Save &amp; End Session".</Typography>
                            </div>
                          )}

                          {!isHistLoading && history !== null && history.length > 0 && (
                            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                              {history.map((h: HistoryRecord, hi: number) => {
                                const dt = new Date(h.changed_at);
                                const dateStr = dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
                                const timeStr = dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                                return (
                                  <div key={h.id}>
                                    {hi > 0 && <Divider style={{ margin: '0 20px' }} />}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 20px' }}>
                                      {/* Commit dot + line */}
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: theme.palette.primary.main, border: `2px solid ${theme.palette.background.paper}`, boxShadow: `0 0 0 2px ${theme.palette.primary.main}`, flexShrink: 0 }} />
                                        {hi < history.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 24, background: theme.palette.divider, marginTop: 4 }} />}
                                      </div>
                                      {/* Commit card */}
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word' }}>
                                          {h.comment}
                                        </Typography>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                                          <Chip
                                            label={h.changed_by}
                                            size="small"
                                            style={{ height: 18, fontSize: 10, background: theme.palette.primary.light, color: theme.palette.primary.contrastText, fontWeight: 600 }}
                                          />
                                          <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                                            {dateStr} at {timeStr}
                                          </Typography>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </Collapse>
                    </div>
                  );
                })}
              </div>
            )}

            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 10 }}>
              Public folders are visible to all users in the "Use Existing Folder" launcher. Private folders are only visible to you.
            </Typography>

            <input
              ref={matlabFolderUploadInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={onMatlabFolderSelected}
              {...({ webkitdirectory: 'true', directory: 'true' } as any)}
            />

            {/* ──────────────────────────────────────────────────────── */}
            {/* Folder Browser Modal */}
            {/* ──────────────────────────────────────────────────────── */}
            {selectedWorkspaceFolder && (
              <Dialog
                open={!!selectedWorkspaceFolder}
                onClose={() => setSelectedWorkspaceFolder(null)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>
                  MATLAB Workspace Browser
                </DialogTitle>
                <DialogContent style={{ minHeight: 300 }}>
                  <Breadcrumbs aria-label="workspace folder navigation" style={{ marginBottom: 12 }}>
                    {[
                      { label: 'Root', path: '' },
                      { label: selectedWorkspaceFolder.folder_name, path: '' },
                      ...currentBrowsePath
                        .split('/')
                        .filter(Boolean)
                        .map((segment, idx, arr) => ({
                          label: segment,
                          path: arr.slice(0, idx + 1).join('/'),
                        })),
                    ].map((crumb, idx, arr) => {
                      const isLast = idx === arr.length - 1;
                      return isLast ? (
                        <span key={`${crumb.label}-${idx}`} style={{ fontWeight: 500 }}>{crumb.label}</span>
                      ) : (
                        <Link
                          key={`${crumb.label}-${idx}`}
                          color="inherit"
                          href="#"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            navigateWorkspaceFolder(selectedWorkspaceFolder, crumb.path);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {crumb.label}
                        </Link>
                      );
                    })}
                  </Breadcrumbs>

                  {browseFolderLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                      <CircularProgress />
                    </div>
                  ) : workspaceFolderContents.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
                      This folder is empty.
                    </Typography>
                  ) : (
                    <TableContainer style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 4 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Size</strong></TableCell>
                            <TableCell align="center"><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {workspaceFolderContents.map((item, idx) => (
                              <TableRow
                                key={`${item.path}-${idx}`}
                                hover
                                style={{ cursor: item.isDirectory ? 'pointer' : 'default' }}
                                onClick={() => {
                                  if (item.isDirectory) {
                                    navigateWorkspaceFolder(selectedWorkspaceFolder, item.path);
                                  }
                                }}
                              >
                                <TableCell>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FolderIcon
                                      fontSize="small"
                                      color={item.isDirectory ? 'action' : 'disabled'}
                                    />
                                    {item.isDirectory ? <strong>{item.name}</strong> : <span>{item.name}</span>}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={item.isDirectory ? 'Folder' : 'File'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  {item.isDirectory
                                    ? '—'
                                    : item.size !== null
                                    ? `${(item.size / 1024).toFixed(1)} KB`
                                    : '—'}
                                </TableCell>
                                <TableCell align="center" onClick={e => e.stopPropagation()}>
                                  {item.isDirectory && (
                                    <Tooltip title="Delete folder">
                                      <IconButton
                                        size="small"
                                        onClick={() => deleteWorkspaceFolder(selectedWorkspaceFolder, item.path)}
                                        disabled={deletingPath === item.path}
                                      >
                                        {deletingPath === item.path ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <DeleteIcon style={{ fontSize: 16, color: theme.palette.error.main }} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!item.isDirectory && selectedWorkspaceFolder?.is_mine && (
                                    <Tooltip title="Delete file">
                                      <IconButton
                                        size="small"
                                        onClick={() => deleteWorkspaceFile(selectedWorkspaceFolder, item.path)}
                                        disabled={deletingPath === item.path}
                                      >
                                        {deletingPath === item.path ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <DeleteIcon style={{ fontSize: 16, color: theme.palette.error.main }} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DialogContent>
                <DialogActions>
                  {selectedWorkspaceFolder?.is_mine && (
                    <>
                      <input
                        ref={workspaceFileUploadRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => {
                          const files = Array.from(e.target.files ?? []);
                          if (files.length > 0) uploadFilesToWorkspace(files);
                        }}
                      />
                      <Tooltip title={selectedWorkspaceFolder.locked_by ? 'End the active session before uploading' : `Upload files into "${currentBrowsePath || 'root'}"`}>
                        <span>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={uploadingToWorkspace ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                            disabled={!!selectedWorkspaceFolder.locked_by || uploadingToWorkspace}
                            onClick={() => workspaceFileUploadRef.current?.click()}
                          >
                            Upload Files
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Button onClick={() => setSelectedWorkspaceFolder(null)}>Close</Button>
                </DialogActions>
              </Dialog>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};


